import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { X, ArrowRight, CheckCircle2, ExternalLink, Sparkles, CreditCard, Key, Plug, Rocket } from "lucide-react";
import { toast } from "sonner";

/**
 * PublishingSetupWizard — guided 5-step Ayrshare onboarding.
 * Triggers at moments of conversion (e.g. clicking "Auto-publish to all" without setup).
 */
export default function PublishingSetupWizard({ workspaceId, brandId, onClose, onConnected }) {
  const [step, setStep] = useState(0);
  const [apiKey, setApiKey] = useState("");
  const [status, setStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [profileKeys, setProfileKeys] = useState({});
  const [enabledPlatforms, setEnabledPlatforms] = useState([]);

  // detect connection state on mount
  useEffect(() => {
    if (!workspaceId) return;
    api.ayrshareStatus(workspaceId).then((s) => {
      setStatus(s);
      if (s.connected) setStep(2); // jump past plan + signup
    }).catch(() => {});
    if (brandId) {
      api.listBrandPlatforms(brandId).then((p) => {
        setEnabledPlatforms((p || []).filter((x) => x.enabled && x.id !== "substack" && ["instagram","facebook","x","linkedin","tiktok","youtube","pinterest","threads","reddit"].includes(x.id)));
      });
    }
  }, [workspaceId, brandId]);

  const connect = async () => {
    if (!apiKey.trim()) return toast.error("Paste the API key first");
    setVerifying(true);
    try {
      const r = await api.ayrshareConnect(workspaceId, apiKey.trim());
      toast.success(`Connected — plan: ${r.plan || "active"}`);
      const s = await api.ayrshareStatus(workspaceId);
      setStatus(s);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "That key didn't work");
    } finally {
      setVerifying(false);
    }
  };

  const saveProfileKey = async (platformId) => {
    const k = (profileKeys[platformId] || "").trim();
    if (!k) return toast.error("Paste a Profile Key");
    await api.setProfileKey(brandId, platformId, k);
    toast.success(`${platformId} connected for this brand`);
  };

  const finish = () => {
    onConnected?.();
    onClose();
  };

  const steps = [
    { id: "intro", label: "Why this & what it costs", icon: Rocket },
    { id: "signup", label: "Create your Ayrshare account", icon: ExternalLink },
    { id: "subscribe", label: "Pick a plan & subscribe", icon: CreditCard },
    { id: "key", label: "Paste your API key", icon: Key },
    { id: "profiles", label: "Connect each platform per brand", icon: Plug },
    { id: "done", label: "All set — start publishing", icon: CheckCircle2 },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="publishing-wizard">
      <div className="bg-white border-2 border-black w-full max-w-4xl brutal-shadow-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-black bg-[#F4F5F6]">
          <div className="flex items-center gap-3">
            <Sparkles size={18} />
            <div>
              <div className="text-eyebrow">Publishing Setup</div>
              <div className="font-display font-bold text-lg tracking-tight">Wire it once. Walk away.</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-wizard"><X size={20} /></button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          <aside className="md:w-64 border-r border-[#E2E4E9] p-3 overflow-y-auto bg-[#FAFAFA]">
            {steps.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <button
                  key={s.id}
                  onClick={() => setStep(i)}
                  data-testid={`wizard-step-${s.id}`}
                  className={`w-full flex items-center gap-3 p-3 text-left border ${active ? "bg-[#0A0A0B] text-white border-[#0A0A0B]" : done ? "border-[#059669] bg-[#F0FDF4]" : "border-transparent hover:bg-[#F4F5F6]"}`}
                >
                  <s.icon size={16} className={done ? "text-[#059669]" : ""} />
                  <div>
                    <div className="text-xs mono uppercase opacity-70">Step {i + 1}</div>
                    <div className="text-sm font-semibold">{s.label}</div>
                  </div>
                </button>
              );
            })}
          </aside>

          <main className="flex-1 overflow-y-auto p-8">
            {step === 0 && <StepIntro onNext={() => setStep(1)} />}
            {step === 1 && <StepSignup onNext={() => setStep(2)} />}
            {step === 2 && <StepSubscribe onNext={() => setStep(3)} />}
            {step === 3 && <StepApiKey apiKey={apiKey} setApiKey={setApiKey} onConnect={connect} verifying={verifying} status={status} />}
            {step === 4 && <StepProfiles enabledPlatforms={enabledPlatforms} profileKeys={profileKeys} setProfileKeys={setProfileKeys} onSave={saveProfileKey} status={status} onNext={() => setStep(5)} />}
            {step === 5 && <StepDone status={status} onFinish={finish} />}
          </main>
        </div>

        <div className="border-t-2 border-black px-6 py-3 flex items-center justify-between bg-[#F4F5F6]">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="text-eyebrow disabled:opacity-30">← Back</button>
          <div className="text-xs mono text-[#A1A1AA]">{step + 1}/{steps.length}</div>
          {step < steps.length - 1 ? (
            <button onClick={() => setStep(step + 1)} data-testid="wizard-next" className="bg-[#0A0A0B] text-white px-5 py-2 text-sm hover:bg-[#002FA7] flex items-center gap-2">
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={finish} data-testid="wizard-finish" className="bg-[#059669] text-white px-5 py-2 text-sm hover:bg-[#047857] flex items-center gap-2">
              Done <CheckCircle2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIntro({ onNext }) {
  return (
    <div>
      <h2 className="font-display font-black text-4xl tracking-tighter">You're 5 steps from auto-posting everywhere.</h2>
      <p className="text-[#52525B] mt-4 max-w-2xl">Substack already auto-publishes from this app via the email we wired. For everything else (Instagram, Facebook, X, LinkedIn, TikTok, YouTube, Pinterest, Threads, Reddit) we need <strong>Ayrshare</strong> — one API key that handles all of them. They handle the OAuth-spaghetti so you never touch it.</p>
      <div className="grid grid-cols-3 gap-3 mt-6 text-sm">
        <Card label="What it costs" value="$149-349/mo" sub="depending on # of brands" />
        <Card label="Platforms unlocked" value="9 auto" sub="(+ Substack already auto)" />
        <Card label="Time to wire" value="~10 min" sub="signup + paste 2 keys" />
      </div>
      <div className="mt-6 border-l-4 border-[#F59E0B] bg-[#FFF7ED] p-3 text-sm">
        <strong>Heads up:</strong> Ayrshare bills you directly (they don't allow resellers below their Enterprise tier). I'll open their checkout in a side tab and bring you right back here — you'll never lose your place.
      </div>
      <button onClick={onNext} data-testid="intro-continue" className="mt-6 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]">Continue</button>
    </div>
  );
}

function StepSignup({ onNext }) {
  return (
    <div>
      <h2 className="font-display font-black text-4xl tracking-tighter">Create your Ayrshare account</h2>
      <p className="text-[#52525B] mt-3 max-w-2xl">Open the signup page in a side tab. Sign up with the same email you use here so we can match it later if you ever need help.</p>
      <a href="https://www.ayrshare.com/?utm_source=vitality_ss" target="_blank" rel="noreferrer"
         data-testid="open-ayrshare-signup"
         className="mt-6 inline-flex items-center gap-2 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]">
        Open Ayrshare signup <ExternalLink size={14} />
      </a>
      <div className="mt-8 border border-[#E2E4E9] p-4 max-w-xl text-sm">
        <div className="text-eyebrow mb-2">Step-by-step (1 minute)</div>
        <ol className="list-decimal pl-5 space-y-2 text-[#52525B]">
          <li>Click "Sign up" (top right).</li>
          <li>Email + password.</li>
          <li>Confirm your email when Ayrshare sends the verification link.</li>
          <li>You'll land on the Ayrshare dashboard. Come back here.</li>
        </ol>
      </div>
      <button onClick={onNext} data-testid="signup-done" className="mt-6 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]">I'm signed up</button>
    </div>
  );
}

function StepSubscribe({ onNext }) {
  return (
    <div>
      <h2 className="font-display font-black text-4xl tracking-tighter">Pick your plan</h2>
      <p className="text-[#52525B] mt-3 max-w-2xl">Inside Ayrshare → click <strong>Billing</strong> (or Plans). Pick the plan based on how many brands you're running.</p>
      <div className="grid md:grid-cols-3 gap-3 mt-6">
        <PlanCard name="Premium" price="$149/mo" desc="1-2 brands · 10 social profiles" highlight={false} />
        <PlanCard name="Business" price="$349/mo" desc="Up to 10 brands · 50 social profiles · recommended for client work" highlight={true} />
        <PlanCard name="Enterprise" price="$599+/mo" desc="100+ profiles · partner pricing" highlight={false} />
      </div>
      <a href="https://www.ayrshare.com/pricing" target="_blank" rel="noreferrer"
         data-testid="open-ayrshare-pricing"
         className="mt-6 inline-flex items-center gap-2 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]">
        Open Ayrshare pricing & subscribe <ExternalLink size={14} />
      </a>
      <div className="mt-6 text-sm text-[#A1A1AA]">Pay there (CC or invoice). Then come back — we'll only need your API key.</div>
      <button onClick={onNext} data-testid="subscribed-done" className="mt-6 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]">I've subscribed</button>
    </div>
  );
}

function StepApiKey({ apiKey, setApiKey, onConnect, verifying, status }) {
  return (
    <div>
      <h2 className="font-display font-black text-4xl tracking-tighter">Paste your Ayrshare API key</h2>
      <p className="text-[#52525B] mt-3 max-w-2xl">In Ayrshare → Dashboard → <strong>API Keys</strong> tab → <strong>Generate Key</strong> (or copy the existing one). Then paste it below.</p>
      <a href="https://app.ayrshare.com/api" target="_blank" rel="noreferrer"
         data-testid="open-ayrshare-keys"
         className="mt-4 inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline">
        Open Ayrshare API keys page <ExternalLink size={12} />
      </a>
      <div className="mt-6 max-w-xl">
        <input
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="paste API key"
          data-testid="ayrshare-key-input"
          className="w-full border-2 border-[#E2E4E9] focus:border-black p-3 outline-none font-mono text-sm"
        />
        <button onClick={onConnect} disabled={verifying || !apiKey.trim()} data-testid="connect-ayrshare-button"
                className="mt-3 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7] disabled:opacity-50 flex items-center gap-2">
          <Plug size={14} /> {verifying ? "Verifying with Ayrshare…" : "Verify & connect"}
        </button>
        {status?.connected && (
          <div className="mt-4 border border-[#059669] bg-[#F0FDF4] p-3 text-sm">
            <strong>Connected.</strong> Plan: {status.plan || "—"}. Active social accounts: {Array.isArray(status.active_social_accounts) ? status.active_social_accounts.join(", ") || "(none yet)" : "—"}
          </div>
        )}
      </div>
    </div>
  );
}

function StepProfiles({ enabledPlatforms, profileKeys, setProfileKeys, onSave, status, onNext }) {
  return (
    <div>
      <h2 className="font-display font-black text-4xl tracking-tighter">Connect this brand's social accounts</h2>
      <p className="text-[#52525B] mt-3 max-w-2xl">Inside Ayrshare → <strong>Profiles</strong> tab → create one Profile per brand, then connect that brand's actual social accounts to it. Copy each Profile's <strong>Profile Key</strong> here so we know which Ayrshare profile is "this brand".</p>
      <a href="https://app.ayrshare.com/profiles" target="_blank" rel="noreferrer"
         data-testid="open-ayrshare-profiles"
         className="mt-4 inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline">
        Open Ayrshare Profiles page <ExternalLink size={12} />
      </a>
      <div className="mt-6 space-y-3 max-w-2xl">
        {enabledPlatforms.length === 0 ? (
          <p className="text-sm text-[#A1A1AA]">No auto-postable platforms are enabled for this brand yet. Enable some in the Platforms tab first, then come back.</p>
        ) : (
          enabledPlatforms.map((p) => (
            <div key={p.id} className="border border-[#E2E4E9] p-3" data-testid={`profile-row-${p.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8" style={{ background: p.color }} />
                <div className="font-semibold">{p.label}</div>
              </div>
              <div className="flex gap-2">
                <input
                  value={profileKeys[p.id] || ""}
                  onChange={(e) => setProfileKeys({ ...profileKeys, [p.id]: e.target.value })}
                  placeholder="Profile Key from Ayrshare (the brand's profile)"
                  data-testid={`profile-key-input-${p.id}`}
                  className="flex-1 border border-[#E2E4E9] focus:border-black p-2 text-sm outline-none font-mono"
                />
                <button onClick={() => onSave(p.id)} data-testid={`save-profile-key-${p.id}`}
                        className="bg-[#0A0A0B] text-white px-3 py-2 text-xs hover:bg-[#002FA7]">Save</button>
              </div>
              <p className="text-xs text-[#52525B] mt-2">Inside Ayrshare → Profiles → click this brand's profile → "Connect {p.label}" → OAuth login as the brand. Then the Profile Key for that profile goes here.</p>
            </div>
          ))
        )}
      </div>
      <button onClick={onNext} data-testid="profiles-done"
              className="mt-6 bg-[#0A0A0B] text-white px-5 py-3 text-sm hover:bg-[#002FA7]">All platforms connected — finish</button>
    </div>
  );
}

function StepDone({ status, onFinish }) {
  return (
    <div className="text-center py-8">
      <CheckCircle2 size={64} className="mx-auto text-[#059669]" fill="#059669" stroke="white" />
      <h2 className="font-display font-black text-4xl tracking-tighter mt-4">You're wired.</h2>
      <p className="text-[#52525B] mt-3 max-w-md mx-auto">Approve any post → click <strong>Auto-publish to all</strong> in the drawer. Substack ships via email, every connected platform ships via Ayrshare. Walk away.</p>
      {status?.connected && (
        <div className="mt-6 inline-block border border-[#059669] bg-[#F0FDF4] px-4 py-2 text-sm">
          Plan: {status.plan || "active"} · {Array.isArray(status.active_social_accounts) ? status.active_social_accounts.length : 0} accounts connected
        </div>
      )}
      <div className="mt-6">
        <button onClick={onFinish} data-testid="finish-wizard" className="bg-[#0A0A0B] text-white px-6 py-3 text-sm hover:bg-[#002FA7]">Start publishing</button>
      </div>
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="border border-[#E2E4E9] p-4">
      <div className="text-eyebrow">{label}</div>
      <div className="font-display font-black text-2xl tracking-tighter mt-1">{value}</div>
      <div className="text-xs text-[#A1A1AA] mt-1">{sub}</div>
    </div>
  );
}

function PlanCard({ name, price, desc, highlight }) {
  return (
    <div className={`border-2 ${highlight ? "border-[#0A0A0B] brutal-shadow" : "border-[#E2E4E9]"} p-4`}>
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg">{name}</div>
        {highlight && <span className="text-xs mono uppercase text-[#002FA7]">Recommended</span>}
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-1">{price}</div>
      <p className="text-xs text-[#52525B] mt-2">{desc}</p>
    </div>
  );
}
